import { serverUrl } from '../config';

/**
 * Unified OAuth Service for platform integrations
 * Supports: Microsoft, Google, Meta, TikTok, and legacy Walmart
 */

/**
 * Start OAuth flow for new platforms (Microsoft, Google, Meta, TikTok)
 * @param {Object} params
 * @param {string} params.platform - Platform identifier (e.g., 'microsoft', 'google', 'meta', 'tiktok')
 * @param {string} params.apiType - API type (e.g., 'ad-api', 'account-api')
 * @param {string} params.eaid - External account ID (required)
 * @param {Object} params.data - Additional platform-specific data
 * @param {Object} params.options - Fetch options (headers, etc.)
 */
const startOAuth = async ({ platform, apiType = 'ad-api', eaid, data = {}, options }) => {
  if (!eaid) {
    throw new Error('External Account ID (eaid) is required');
  }

  try {
    const url = `${serverUrl}/onboarding/oauth/start`;
    const response = await fetch(url, {
      method: 'POST',
      ...options,
      body: JSON.stringify({
        platform,
        apiType,
        eaid,
        ...data,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text);
    }

    const result = await response.json();

    // Redirect to OAuth provider
    if (result.oauthUrl) {
      window.location.href = result.oauthUrl;
    }

    return result;
  } catch (error) {
    console.error('OAuth start error:', error);
    throw error;
  }
};

/**
 * Complete OAuth flow (exchange code for token)
 * @param {Object} params
 * @param {string} params.platform - Platform identifier
 * @param {string} params.apiType - API type
 * @param {string} params.code - OAuth authorization code
 * @param {string} params.state - OAuth state parameter
 * @param {Object} params.additionalParams - Other URL parameters
 * @param {Object} params.options - Fetch options
 */
const completeOAuth = async ({ platform, apiType = 'ad-api', code, state, additionalParams = {}, options }) => {
  try {
    const url = `${serverUrl}/onboarding/oauth/complete`;
    const response = await fetch(url, {
      method: 'POST',
      ...options,
      body: JSON.stringify({
        platform,
        apiType,
        code,
        state,
        ...additionalParams,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text);
    }

    return await response.json();
  } catch (error) {
    console.error('OAuth complete error:', error);
    throw error;
  }
};

/**
 * Check if account already has platform/API authorizations
 * Used for showing warnings about dependent APIs
 * @param {Object} params
 * @param {string} params.eaid - External Account ID to check
 * @param {string} params.platform - Platform to check
 * @param {Object} params.options - Fetch options
 */
const checkExistingAuthorizations = async ({ eaid, platform, options }) => {
  try {
    const url = `${serverUrl}/onboarding/oauth/check?eaid=${eaid}&platform=${platform}`;
    const response = await fetch(url, {
      method: 'GET',
      ...options,
    });

    if (!response.ok) {
      return { hasAuthorizations: false, authorizations: [] };
    }

    return await response.json();
  } catch (error) {
    console.error('Check authorizations error:', error);
    return { hasAuthorizations: false, authorizations: [] };
  }
};

// ============================================================================
// Legacy Walmart OAuth Methods (keep for backward compatibility)
// ============================================================================

const wmMpOauth = async ({
  options,
  onSuccess,
  onError,
  redirectOnSuccess = true,
}) => {
  try {
    const onboardStartUrl = `${serverUrl}/onboarding/walmart/mp-api/start`;
    const redirectUri = 'https://dashboard.limitless.tech/onboarding/oauth/walmart';
    const response = await fetch(onboardStartUrl, options);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text);
    }

    const data = await response.json();

    if (data.referenceId && data.referenceId > 0) {
      if (redirectOnSuccess) {
        const redirectLink = `https://login.account.wal-mart.com/authorize?responseType=code&clientId=${data.clientId}&redirectUri=${redirectUri}&nonce=${data.referenceId}&state=${data.referenceId}&clientType=seller`;
        window.location.href = redirectLink;
      }
    } else {
      throw new Error('We are having issues processing your request, please try again later. ERROR 00003');
    }

    onSuccess?.(data);
  } catch (error) {
    onError?.(error);
  }
};

const wmMpDisconnect = async ({
  options,
  onSuccess,
  onError,
}) => {
  try {
    const disconnectUrl = `${serverUrl}/onboarding/walmart/mp-api/disconnect`;
    const response = await fetch(disconnectUrl, options);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text);
    }

    const data = await response.json();

    if (data && data.status === 'SUCCESS') {
      console.info('Disconect Complete');
    } else {
      throw new Error('We are having issues processing your request, please try again later. ERROR 00004');
    }

    onSuccess?.(data);
  } catch (error) {
    onError?.(error);
  }
};

const oauthService = {
  // New unified methods
  startOAuth,
  completeOAuth,
  checkExistingAuthorizations,
  // Legacy Walmart methods
  wmMpOauth,
  wmMpDisconnect,
};

export default oauthService;
